import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGetStaffBasicDataQuery } from "../../../../store/queries/Staff";
import {
  getBusinessLocalNow,
  getEpochMsFromBusinessLocal,
} from "../../../../utils/Functions/table";
import { posistDeltaBulkSync, posistDeltaRunStatus } from "../api";

// A store takes 35-70s (Posist returns the whole menu per store regardless of
// the sync window), so polling faster than this just burns requests.
const POLL_INTERVAL_MS = 4000;

// The window the API takes is epoch milliseconds, but nobody can read or type
// one, so the field is a date and time picker and the conversion happens here.
// Both ends are held to what the server will accept: it refuses anything under
// 13 digits (a window opening in 1970, i.e. the whole menu of every selected
// store) and anything in the future (a window that ends before it starts, since
// the other end is always now).
const EARLIEST_EPOCH_MS = 1000000000000;

const useDeltaMenuSync = ({ show }) => {
  const { data: storeData = {} } = useGetStaffBasicDataQuery();

  const [selectedStores, setSelectedStores] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [lastSyncTime, setLastSyncTime] = useState("");
  const [run, setRun] = useState(null);
  const [isStarting, setIsStarting] = useState(false);

  // Kept in refs, not state: the poll loop reads them across timeouts and must
  // not re-subscribe (or capture a stale value) every render.
  const runIdRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const stores = storeData?.data?.stores ?? [];

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // setTimeout chain rather than setInterval: a slow status call must not have a
  // second one stacked behind it.
  const poll = useCallback(async () => {
    const runId = runIdRef.current;
    if (!runId) return;

    const response = await posistDeltaRunStatus(runId);

    // Bail if the modal closed or a different run started while this was in flight.
    if (stoppedRef.current || runIdRef.current !== runId) return;

    const body = response?.data;

    if (body?.status_code === 200) {
      setRun(body?.data);
      // Every store reached a terminal state - nothing left to watch.
      if (body?.data?.complete) return;
    } else if (body?.status_code === 422) {
      toast.error(body?.message || "Could not read the sync status");
      return;
    }
    // Anything else (a network blip, a 500) is treated as transient: keep polling,
    // since the worker is still chewing through the stores regardless.

    timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, []);

  // Every store the picker offers. Selecting them all is a long run - roughly a
  // minute each, one at a time on the drain cron - so the button says how many.
  const handleSelectAll = () => setSelectedStores(stores);

  const handleClearAll = () => setSelectedStores([]);

  const handleStart = async () => {
    if (!selectedStores.length) return;

    const email = notifyEmail.trim();
    const windowStart = getEpochMsFromBusinessLocal(lastSyncTime);

    // Checked here as well as on the server: both inputs are optional, and
    // finding out a typo'd address was rejected means re-running a sync that
    // takes minutes per store.
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email address, or leave it blank");
      return;
    }

    // The picker's own min/max cover the mouse; these cover a date typed straight
    // into the field, which browsers let through.
    if (lastSyncTime && windowStart === null) {
      toast.error("Enter a valid date and time, or leave it blank");
      return;
    }

    if (windowStart !== null && windowStart < EARLIEST_EPOCH_MS) {
      toast.error("Pick a date from 2002 onwards, or leave it blank");
      return;
    }

    if (windowStart !== null && windowStart > Date.now()) {
      toast.error("The sync window has to start in the past, not the future");
      return;
    }

    setIsStarting(true);
    clearTimer();
    stoppedRef.current = false;

    const response = await posistDeltaBulkSync({
      customer_keys: selectedStores.map((store) => store?.customer_key),
      // Omitted rather than sent empty: an absent key is what tells the server
      // to resume from each store's own watermark.
      ...(email ? { notify_email: email } : {}),
      ...(windowStart !== null ? { last_sync_time: String(windowStart) } : {}),
    });
    const body = response?.data;

    if (body?.status_code === 200) {
      runIdRef.current = body?.data?.run_id;

      // Seed the table from the dispatch response so every store shows as pending
      // straight away, instead of an empty panel until the first poll lands.
      setRun({
        run_id: body?.data?.run_id,
        total: body?.data?.total,
        notify_email: body?.data?.notify_email,
        last_sync_time: body?.data?.last_sync_time,
        complete: false,
        counts: {
          pending: body?.data?.total ?? 0,
          running: 0,
          success: 0,
          failed: 0,
          skipped: 0,
        },
        stores: (body?.data?.stores ?? []).map((store) => ({
          ...store,
          status: "pending",
        })),
      });

      toast.success(
        `Delta sync queued for ${body?.data?.total} store${
          body?.data?.total === 1 ? "" : "s"
        }`
      );

      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } else {
      toast.error(body?.message || "Something went wrong");
    }

    setIsStarting(false);
  };

  // Back to the picker for another run. The finished run stays on the server.
  // The email is deliberately kept: the next run is usually the operator
  // re-trying the stores that failed, and they still want telling about it.
  const handleStartAnother = () => {
    clearTimer();
    stoppedRef.current = true;
    runIdRef.current = null;
    setRun(null);
    setSelectedStores([]);
    setLastSyncTime("");
  };

  useEffect(() => {
    if (show) {
      // Reopening while a run is still going picks the watch back up - the work
      // is on the worker, so closing the modal never cancelled anything.
      if (runIdRef.current && !run?.complete) {
        stoppedRef.current = false;
        clearTimer();
        poll();
      }
    } else {
      stoppedRef.current = true;
      clearTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(
    () => () => {
      stoppedRef.current = true;
      clearTimer();
    },
    [clearTimer]
  );

  const finishedCount =
    (run?.counts?.success ?? 0) +
    (run?.counts?.failed ?? 0) +
    (run?.counts?.skipped ?? 0);

  return {
    stores,
    selectedStores,
    setSelectedStores,
    notifyEmail,
    setNotifyEmail,
    lastSyncTime,
    setLastSyncTime,
    // Recomputed per render rather than pinned when the modal opened, so a panel
    // left sitting all afternoon still refuses only genuinely future instants.
    maxSyncTime: getBusinessLocalNow(),
    run,
    isStarting,
    finishedCount,
    handleSelectAll,
    handleClearAll,
    handleStart,
    handleStartAnother,
  };
};

export default useDeltaMenuSync;
